/**
 * StrikeHotspotMap
 * --------------------------------------------------------------------------
 * Geo-spatial heatmap of wildlife-strike events on an interactive map.
 *
 * Why this exists
 *   Once strikes are geo-tagged at capture time (PhotoCapture geoTag=true)
 *   we can surface bird-prone runway thresholds at a glance — answering
 *   "where exactly are we losing the war?" with a single heatmap rather
 *   than a CSV of lat/lon strings.
 *
 * Stack notes
 *   - Leaflet (open source, no API key required vs. Mapbox)
 *   - OpenStreetMap base tiles
 *   - CircleMarker overlay sized & coloured by damage level
 *   - The MapContainer is wrapped in a single-mount guard because React 18+
 *     StrictMode double-mounts effects in dev and Leaflet rejects the
 *     second call with "Map container is already initialized". Fix: wrap
 *     MapContainer in a stable `<SingleMount>` that only renders its
 *     children on the first useEffect run.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet.heat';
import { MapContainer, TileLayer, CircleMarker, Tooltip, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

// React StrictMode double-mounts effects in dev which causes Leaflet's
// internal _initContainer to throw "Map container is already initialized"
// because the cleanup of the first mount leaves a stale `_leaflet_id` on
// the DOM node. Patch _initContainer once (module-load) to clear that
// stale id and continue, instead of throwing. This is a no-op in prod
// builds (StrictMode only double-mounts in dev) but makes dev usable.
if (typeof L !== 'undefined' && L.Map && L.Map.prototype && !L.Map.prototype.__strictModePatched) {
  const original = L.Map.prototype._initContainer;
  L.Map.prototype._initContainer = function (id) {
    const container = L.DomUtil.get(id);
    if (container && container._leaflet_id) {
      // eslint-disable-next-line no-underscore-dangle
      container._leaflet_id = null;
    }
    return original.call(this, id);
  };
  L.Map.prototype.__strictModePatched = true;
}

const DAMAGE_COLOR = {
  none: '#10b981',
  minor: '#facc15',
  substantial: '#f97316',
  destroyed: '#ef4444',
  unknown: '#94a3b8',
};

const DAMAGE_RADIUS = {
  none: 6,
  minor: 8,
  substantial: 10,
  destroyed: 14,
  unknown: 6,
};

const FALLBACK_CENTER = [24.7136, 46.6753]; // Riyadh
const FALLBACK_ZOOM = 5;

function FitToPoints({ points }) {
  const map = useMap();
  const fitted = useRef(false);
  useEffect(() => {
    if (fitted.current) return;
    if (!points || points.length === 0) return;
    const bounds = points.map((p) => [p.lat, p.lon]);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    fitted.current = true;
  }, [points, map]);
  return null;
}

/**
 * HeatLayer
 *   Wraps the leaflet.heat plugin in a react-leaflet child component.
 *   Weighting is driven by damage severity so a single "destroyed"
 *   strike contributes far more density than a "no-damage" sparrow,
 *   which matches how Annex 14 Wildlife Hazard Management programmes
 *   prioritise mitigation effort.
 */
const DAMAGE_WEIGHT = {
  none: 0.2,
  minor: 0.45,
  substantial: 0.8,
  destroyed: 1.0,
  unknown: 0.3,
};

function HeatLayer({ points }) {
  const map = useMap();
  const layerRef = useRef(null);
  useEffect(() => {
    if (!points || points.length === 0) return undefined;
    const heatPoints = points.map((p) => [
      p.lat,
      p.lon,
      DAMAGE_WEIGHT[p.damage_level || 'unknown'] || 0.3,
    ]);
    layerRef.current = L.heatLayer(heatPoints, {
      radius: 28,
      blur: 22,
      maxZoom: 13,
      max: 1.0,
      minOpacity: 0.35,
      gradient: {
        0.2: '#10b981',  // emerald
        0.4: '#facc15',  // amber
        0.6: '#f97316',  // orange
        0.8: '#ef4444',  // red
        1.0: '#7f1d1d',  // dark red — runway-side hotspot
      },
    }).addTo(map);
    return () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [points, map]);
  return null;
}

/**
 * SingleMount delays rendering its children until *after* React StrictMode's
 * first synchronous mount/unmount cycle has settled. The `mounted` flag
 * survives the StrictMode reconciliation because it lives in a useState
 * tuple that React preserves across the simulated unmount.
 */
function SingleMount({ children }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // Wait one microtask + one frame so the StrictMode dev-only second
    // mount has a chance to finalise before we instantiate Leaflet.
    let cancelled = false;
    Promise.resolve().then(() =>
      requestAnimationFrame(() => {
        if (!cancelled) setMounted(true);
      })
    );
    return () => { cancelled = true; };
  }, []);
  if (!mounted) {
    return (
      <div className="h-full w-full flex items-center justify-center text-slate-500 text-sm">
        Initialising map…
      </div>
    );
  }
  return children;
}

export default function StrikeHotspotMap({ points = [], height = 480 }) {
  // 'markers' | 'heatmap' | 'both'
  const [viewMode, setViewMode] = useState('both');
  // Time-of-day filter: null = "All hours", 0..23 = filter to that hour ±0
  const [hourFilter, setHourFilter] = useState(null);
  const [playing, setPlaying] = useState(false);
  const playRef = useRef(null);

  // Derived: list of points whose `incident_time` (HH:MM) hour matches the filter.
  // When hourFilter is null, return all points unchanged. Strikes without an
  // incident_time pass through under "All hours" but are excluded once the
  // user starts filtering — they have no temporal signal to contribute.
  const filteredPoints = useMemo(() => {
    if (hourFilter == null) return points;
    return points.filter((p) => {
      const t = p.incident_time;
      if (!t || typeof t !== 'string') return false;
      const hh = parseInt(t.slice(0, 2), 10);
      return Number.isFinite(hh) && hh === hourFilter;
    });
  }, [points, hourFilter]);

  // Auto-play: advance hourFilter every 800ms, looping 0→23 then stopping
  useEffect(() => {
    if (!playing) {
      if (playRef.current) {
        clearInterval(playRef.current);
        playRef.current = null;
      }
      return undefined;
    }
    playRef.current = setInterval(() => {
      setHourFilter((h) => {
        const next = h == null ? 0 : h + 1;
        if (next > 23) {
          setPlaying(false);
          return 23;
        }
        return next;
      });
    }, 800);
    return () => {
      if (playRef.current) clearInterval(playRef.current);
    };
  }, [playing]);

  const stats = useMemo(() => {
    const byDamage = filteredPoints.reduce((acc, p) => {
      const k = p.damage_level || 'unknown';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const byAirport = filteredPoints.reduce((acc, p) => {
      const k = p.airport_code || 'UNKN';
      acc[k] = (acc[k] || 0) + 1;
      return acc;
    }, {});
    const topAirport = Object.entries(byAirport).sort((a, b) => b[1] - a[1])[0];
    return {
      total: filteredPoints.length,
      byDamage,
      topAirport: topAirport ? { code: topAirport[0], count: topAirport[1] } : null,
    };
  }, [filteredPoints]);

  // Hour-of-day histogram across the FULL dataset (not filtered) — used to
  // visually mark "active hours" on the slider track so users see at a
  // glance which hours have data before scrubbing.
  const hourHistogram = useMemo(() => {
    const hist = new Array(24).fill(0);
    points.forEach((p) => {
      const t = p.incident_time;
      if (!t || typeof t !== 'string') return;
      const hh = parseInt(t.slice(0, 2), 10);
      if (Number.isFinite(hh) && hh >= 0 && hh <= 23) hist[hh] += 1;
    });
    const max = Math.max(1, ...hist);
    return { hist, max };
  }, [points]);

  if (points.length === 0) {
    return (
      <div
        className="rounded-lg border border-slate-700 bg-slate-900/40 flex flex-col items-center justify-center text-center p-8"
        style={{ height }}
        data-testid="hotspot-empty-state"
      >
        <div className="text-slate-400 text-sm font-medium mb-1">No geo-tagged strikes yet</div>
        <div className="text-slate-500 text-xs max-w-md">
          Strike points will populate this map automatically as ramp staff submit reports
          with photos taken on a device with location services enabled.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="hotspot-map-container">
      {/* 24-hour time-of-day playback — shows when bird-strike activity peaks */}
      <div
        className="rounded-md border border-slate-700 bg-slate-900/40 p-3"
        data-testid="hotspot-time-slider"
      >
        <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="text-xs px-2.5 py-1 rounded-md border border-slate-700 hover:border-sky-500/60 text-slate-200 inline-flex items-center gap-1.5"
              data-testid="hotspot-time-play"
            >
              {playing ? '⏸ Pause' : '▶ Play 24h'}
            </button>
            <button
              type="button"
              onClick={() => { setPlaying(false); setHourFilter(null); }}
              className={`text-xs px-2.5 py-1 rounded-md border ${
                hourFilter == null
                  ? 'bg-sky-500/20 border-sky-500/60 text-sky-200'
                  : 'border-slate-700 text-slate-400 hover:border-slate-500'
              }`}
              data-testid="hotspot-time-all"
            >
              All hours
            </button>
            <span className="text-xs text-slate-400 tabular-nums ml-1">
              {hourFilter == null
                ? `${points.length} strikes total`
                : `${String(hourFilter).padStart(2, '0')}:00–${String(hourFilter).padStart(2, '0')}:59 · ${filteredPoints.length} strike${filteredPoints.length === 1 ? '' : 's'}`}
            </span>
          </div>
          {hourHistogram.hist.some((h) => h > 0) && (
            <span className="text-[10px] text-slate-500 uppercase tracking-wider">
              Activity by hour ↓
            </span>
          )}
        </div>

        {/* Histogram track — bar chart of strike counts per hour */}
        <div className="grid grid-cols-24 gap-px h-8 mb-1" style={{ gridTemplateColumns: 'repeat(24, minmax(0, 1fr))' }}>
          {hourHistogram.hist.map((c, h) => {
            const pct = (c / hourHistogram.max) * 100;
            const active = hourFilter === h;
            return (
              <button
                key={h}
                type="button"
                onClick={() => { setPlaying(false); setHourFilter(h); }}
                className="relative h-full bg-slate-800/40 hover:bg-slate-700 rounded-sm transition-colors"
                data-testid={`hotspot-time-hour-${h}`}
                title={`${String(h).padStart(2, '0')}:00 — ${c} strike${c === 1 ? '' : 's'}`}
              >
                <span
                  className={`absolute bottom-0 left-0 right-0 ${active ? 'bg-sky-400' : c > 0 ? 'bg-sky-500/60' : 'bg-slate-700/30'}`}
                  style={{ height: `${pct}%` }}
                />
              </button>
            );
          })}
        </div>
        <input
          type="range"
          min={0}
          max={23}
          step={1}
          value={hourFilter == null ? 0 : hourFilter}
          onChange={(e) => { setPlaying(false); setHourFilter(Number(e.target.value)); }}
          className="w-full accent-sky-500"
          data-testid="hotspot-time-range"
          aria-label="Time of day filter (hour)"
        />
        <div className="grid grid-cols-4 text-[10px] text-slate-500 tabular-nums mt-1">
          <span>00:00</span>
          <span className="text-center">06:00</span>
          <span className="text-center">12:00</span>
          <span className="text-right">18:00 → 23:59</span>
        </div>
      </div>

      {/* View-mode toggle (Markers / Heatmap / Both) */}
      <div className="flex items-center justify-end gap-1" data-testid="hotspot-view-toggle">
        {[
          { id: 'markers', label: 'Markers' },
          { id: 'heatmap', label: 'Heatmap' },
          { id: 'both', label: 'Both' },
        ].map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => setViewMode(opt.id)}
            className={`text-xs px-3 py-1.5 rounded-md border transition-colors ${
              viewMode === opt.id
                ? 'bg-sky-500/20 border-sky-500/60 text-sky-200'
                : 'bg-slate-900/40 border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
            data-testid={`hotspot-view-${opt.id}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Quick-glance stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Geo-tagged Strikes</div>
          <div className="text-2xl font-bold text-sky-300 tabular-nums">{stats.total}</div>
        </div>
        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Hotspot Airport</div>
          <div className="text-2xl font-bold text-violet-300">
            {stats.topAirport ? stats.topAirport.code : '—'}
            {stats.topAirport && (
              <span className="text-xs text-slate-500 ml-2 font-normal">
                ({stats.topAirport.count})
              </span>
            )}
          </div>
        </div>
        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">Substantial / Destroyed</div>
          <div className="text-2xl font-bold text-rose-400 tabular-nums">
            {(stats.byDamage.substantial || 0) + (stats.byDamage.destroyed || 0)}
          </div>
        </div>
        <div className="rounded-md border border-slate-700 bg-slate-900/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-400">No Damage</div>
          <div className="text-2xl font-bold text-emerald-400 tabular-nums">
            {stats.byDamage.none || 0}
          </div>
        </div>
      </div>

      {/* Map */}
      <div
        className="rounded-lg overflow-hidden border border-slate-700 relative"
        style={{ height }}
        data-testid="hotspot-leaflet-canvas"
      >
        <SingleMount>
          <MapContainer
            center={FALLBACK_CENTER}
            zoom={FALLBACK_ZOOM}
            scrollWheelZoom={true}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitToPoints points={filteredPoints.length ? filteredPoints : points} />
            {(viewMode === 'heatmap' || viewMode === 'both') && (
              <HeatLayer points={filteredPoints} />
            )}
            {(viewMode === 'markers' || viewMode === 'both') && filteredPoints.map((p, i) => {
              const color = DAMAGE_COLOR[p.damage_level || 'unknown'] || DAMAGE_COLOR.unknown;
              const radius = DAMAGE_RADIUS[p.damage_level || 'unknown'] || 6;
              return (
                <CircleMarker
                  key={`${p.strike_id}-${i}`}
                  center={[p.lat, p.lon]}
                  radius={radius}
                  pathOptions={{
                    color,
                    fillColor: color,
                    fillOpacity: 0.55,
                    weight: 1.5,
                  }}
                >
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}>
                    <div className="text-xs">
                      <div className="font-bold">
                        {p.airport_code}
                        {p.runway ? ` · R/W ${p.runway}` : ''}
                      </div>
                      <div>{p.species_name || p.wildlife_category || 'Wildlife'}</div>
                      <div className="text-slate-500">
                        {p.incident_date} · {p.damage_level} damage
                      </div>
                      {p.accuracy != null && (
                        <div className="text-slate-500">±{Math.round(p.accuracy)}m ({p.source})</div>
                      )}
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </SingleMount>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-xs text-slate-400" data-testid="hotspot-legend">
        {Object.entries(DAMAGE_COLOR).map(([k, c]) => (
          <div key={k} className="flex items-center gap-2">
            <span
              className="inline-block w-3 h-3 rounded-full border border-slate-600"
              style={{ backgroundColor: c }}
            />
            <span className="capitalize">{k}</span>
            <span className="text-slate-500 tabular-nums">({stats.byDamage[k] || 0})</span>
          </div>
        ))}
      </div>
    </div>
  );
}
